import UsernameInput from "./UsernameInput";

export default function BasicInfo({ userData, onChange, originalUsername, fieldErrors = {} }) {
  return (
    <>
      {/* Basic Information */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 flex items-center gap-2">
            Account Details
          </h3>
          <p className="mt-1 text-xs sm:text-sm text-gray-600">
            Your basic account information
          </p>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
            <UsernameInput 
              value={userData.username}
              onChange={onChange}
              originalUsername={originalUsername}
              error={fieldErrors.username} 
            />

            <div className="space-y-1 sm:space-y-2">
              <label
                htmlFor="name"
                className="block text-xs sm:text-sm font-semibold text-gray-700"
              >
                Name
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={userData.name}
                onChange={onChange}
                className={`block w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-gray-50 focus:bg-white ${
                  fieldErrors.name ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                }`}
                placeholder="Your display name"
              />
              {fieldErrors.name && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.name}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
            <div className="space-y-1 sm:space-y-2">
              <label
                htmlFor="birthday"
                className="block text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1 sm:gap-2"
              >
                Date of Birth
                <span className="text-gray-400 text-xs ml-1">(Fixed)</span>
              </label>
              <div className="relative">
                <input
                  type="date"
                  name="birthday"
                  id="birthday"
                  value={userData.birthday}
                  readOnly
                  disabled
                  className="block w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm border border-gray-200 rounded-lg sm:rounded-xl bg-gray-50 text-gray-600 cursor-not-allowed opacity-70"
                />
                <div className="absolute inset-0 bg-gray-50/20 rounded-lg sm:rounded-xl pointer-events-none" />
              </div>
              <p className="text-xs text-gray-500">This information cannot be modified</p>
            </div>

            <div className="space-y-1 sm:space-y-2">
              <label
                htmlFor="gender"
                className="block text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1 sm:gap-2"
              >
                Gender
              </label>
              <select
                name="gender"
                id="gender"
                value={userData.gender || ""}
                onChange={onChange}
                className={`block w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-gray-50 focus:bg-white ${
                  fieldErrors.gender ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                }`}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non_binary">Non-binary</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
              </select>
              {fieldErrors.gender && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.gender}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Profile Information */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 flex items-center gap-2">
            Profile Information
          </h3>
          <p className="mt-1 text-xs sm:text-sm text-gray-600">
            Tell your story and share your interests
          </p>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          <div className="space-y-1 sm:space-y-2">
            <label
              htmlFor="bio"
              className="block text-xs sm:text-sm font-semibold text-gray-700"
            >
              Bio
            </label>
            <textarea
              id="bio"
              name="bio"
              rows={3}
              maxLength={200}
              value={userData.bio}
              onChange={onChange}
              className={`block w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-gray-50 focus:bg-white resize-none sm:rows-4 ${
                fieldErrors.bio ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
              }`}
              placeholder="Write something about yourself..."
            />
            <div className="flex justify-between items-center">
              <p className="text-xs text-gray-500">
                {userData.bio.length}/200 characters
              </p>
              {fieldErrors.bio && (
                <p className="text-xs text-red-600">{fieldErrors.bio}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
            <div className="space-y-1 sm:space-y-2">
              <label
                htmlFor="location"
                className="block text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1 sm:gap-2"
              >
                Location
              </label>
              <input
                type="text"
                name="location"
                id="location"
                value={userData.location}
                onChange={onChange}
                className={`block w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-gray-50 focus:bg-white ${
                  fieldErrors.location ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                }`}
                placeholder="Where are you based?"
              />
              {fieldErrors.location && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.location}</p>
              )}
            </div>

            <div className="space-y-1 sm:space-y-2">
              <label
                htmlFor="website"
                className="block text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1 sm:gap-2"
              >
                Website
              </label>
              <input
                type="text"
                name="website"
                id="website"
                value={userData.website}
                onChange={onChange}
                className={`block w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-gray-50 focus:bg-white ${
                  fieldErrors.website ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                }`}
                placeholder="https://yourwebsite.com"
              />
              {fieldErrors.website && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.website}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}